import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import AnagraficaPicker from "@/components/AnagraficaPicker";
import CurrencyInput, { formatCurrencyIT } from "@/components/CurrencyInput";

const PROVV_TEXT = "text-emerald-600";
const PROVV_BG = "bg-emerald-100";
const PROVV_BORDER = "border-emerald-200";

const formatEUR = (n) =>
  n === null || n === undefined || n === "" ? "—" : `${formatCurrencyIT(n, false)} €`;

const STATO_LABELS = {
  in_attesa: "In attesa",
  approvato: "Approvato",
  erogato: "Erogato",
  rifiutato: "Rifiutato",
};

const STATO_COLORS = {
  in_attesa: "bg-amber-100 text-amber-800",
  approvato: "bg-blue-100 text-blue-700",
  erogato: "bg-emerald-100 text-emerald-700",
  rifiutato: "bg-rose-100 text-rose-700",
};

/** Sprint 4F1.3 — Popup completo Finanziamento con 7 sezioni. */
export default function FinanziamentoPopup({ open, onOpenChange, initial, onSave, contextCompanyId }) {
  const [form, setForm] = useState(emptyForm());
  const [show2, setShow2] = useState(false);

  useEffect(() => {
    if (!open) return;
    const f = initial?.intestatario_id ? { ...emptyForm(), ...initial } : emptyForm();
    // Preserve rate_periodo_1 properly
    f.rate_periodo_1 = initial?.rate_periodo_1 || emptyForm().rate_periodo_1;
    f.rate_periodo_2 = initial?.rate_periodo_2 || null;
    setForm(f);
    setShow2(!!initial?.rate_periodo_2);
  }, [open, initial]);

  const setRP1 = (patch) =>
    setForm((f) => ({ ...f, rate_periodo_1: { ...f.rate_periodo_1, ...patch } }));
  const setRP2 = (patch) =>
    setForm((f) => ({ ...f, rate_periodo_2: { ...(f.rate_periodo_2 || emptyForm().rate_periodo_2), ...patch } }));

  const provv_tipo = form.provvigioni_tipo;
  const isParziali = provv_tipo === "parziali";
  const isTotale = provv_tipo === "totale";

  // Sprint 4F1.4: quando si attiva Parziali la prima volta, pre-popola
  // proventi_spese_pratica = spese_istruttoria (era pre-popolato solo nel
  // visual ma non nel form, causando bug nella somma).
  useEffect(() => {
    if (isParziali && (form.proventi_spese_pratica === null
        || form.proventi_spese_pratica === undefined)) {
      setForm((f) => ({ ...f, proventi_spese_pratica: Number(f.spese_istruttoria || 0) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParziali]);

  // Anteprima ass calc
  const ass_imp = Number(form.importo_assicurazione || 0);
  const ass_tipo = form.proventi_assicurazione_tipo;
  const ass_perc = Number(form.proventi_assicurazione_percentuale);
  const ass_fix = Number(form.proventi_assicurazione_fisso);
  const ass_perc_valid =
    ass_tipo === "percentuale" &&
    form.proventi_assicurazione_percentuale !== "" &&
    form.proventi_assicurazione_percentuale !== null &&
    !isNaN(ass_perc) && ass_perc > 0 && ass_perc <= 100;
  const ass_fix_valid =
    ass_tipo === "fisso" &&
    form.proventi_assicurazione_fisso !== "" &&
    form.proventi_assicurazione_fisso !== null &&
    !isNaN(ass_fix) && ass_fix >= 0;
  const ass_calc = ass_perc_valid ? Math.round(ass_imp * (ass_perc / 100) * 100) / 100
                  : ass_fix_valid ? Math.round(ass_fix * 100) / 100 : 0;

  // Anteprima totale proventi
  const totale_proventi = isTotale
    ? (Number(form.provvigione_lorda_totale) || 0)
    : isParziali
      ? (Number(form.proventi_finanziamento || 0)
         + Number(form.proventi_spese_pratica || 0)
         + ass_calc)
      : 0;

  // Validation
  const canSubmit =
    !!form.intestatario_id &&
    !!form.finanziaria_id &&
    (form.prodotto_descrizione || "").trim().length >= 1 &&
    Number(form.rate_periodo_1?.numero_rate || 0) > 0 &&
    !!provv_tipo &&
    (isTotale
      ? form.provvigione_lorda_totale !== "" && form.provvigione_lorda_totale !== null
      : isParziali ? true : false);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload = {
      intestatario_id: form.intestatario_id,
      garante_id: form.garante_id || null,
      finanziaria_id: form.finanziaria_id,
      prodotto_descrizione: (form.prodotto_descrizione || "").trim(),
      numero_pratica: (form.numero_pratica || "").trim(),
      importo_finanziato: Number(form.importo_finanziato || 0),
      spese_istruttoria: Number(form.spese_istruttoria || 0),
      importo_assicurazione: Number(form.importo_assicurazione || 0),
      compagnia_assicurativa: (form.compagnia_assicurativa || "").trim(),
      durata_copertura_mesi: Number(form.durata_copertura_mesi || 0) || 0,
      descrizione_assicurazione: (form.descrizione_assicurazione || "").trim(),
      rate_periodo_1: {
        numero_rate: Number(form.rate_periodo_1.numero_rate || 0),
        importo_rata: Number(form.rate_periodo_1.importo_rata || 0),
        tan_percent: Number(form.rate_periodo_1.tan_percent || 0),
        taeg_percent: Number(form.rate_periodo_1.taeg_percent || 0),
        maxirata: form.rate_periodo_1.maxirata
          ? Number(form.rate_periodo_1.maxirata) : null,
      },
      rate_periodo_2: show2 && form.rate_periodo_2 ? {
        numero_rate: Number(form.rate_periodo_2.numero_rate || 0),
        importo_rata: Number(form.rate_periodo_2.importo_rata || 0),
        tan_percent: Number(form.rate_periodo_2.tan_percent || 0),
        taeg_percent: Number(form.rate_periodo_2.taeg_percent || 0),
      } : null,
      provvigioni_tipo: provv_tipo,
      provvigione_lorda_totale: isTotale ? Number(form.provvigione_lorda_totale || 0) : null,
      proventi_finanziamento: isParziali
        ? (form.proventi_finanziamento === "" ? null : Number(form.proventi_finanziamento)) : null,
      proventi_spese_pratica: isParziali
        ? (form.proventi_spese_pratica === "" ? null : Number(form.proventi_spese_pratica)) : null,
      proventi_assicurazione_tipo: isParziali ? (ass_tipo || null) : null,
      proventi_assicurazione_percentuale: isParziali && ass_tipo === "percentuale"
        ? Number(ass_perc) : null,
      proventi_assicurazione_fisso: isParziali && ass_tipo === "fisso"
        ? Number(ass_fix) : null,
      status_finanziamento: form.status_finanziamento || "in_attesa",
      data: form.data || null,
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="fin-popup">
        <DialogHeader>
          <DialogTitle>Finanziamento</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2 text-sm">
          {/* ═══ ANAGRAFICHE ═══ */}
          <div data-testid="fin-section-anagrafiche">
            <h3 className="text-lg font-bold text-slate-900 mb-3 pb-1 border-b">Anagrafiche</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Intestatario del finanziamento <span className="text-rose-600">*</span></Label>
                <AnagraficaPicker
                  value={form.intestatario_id || null}
                  onChange={(id) => setForm({ ...form, intestatario_id: id })}
                  contextCompanyId={contextCompanyId || null}
                  testIdPrefix="fin-intestatario"
                />
              </div>
              <div>
                <Label className="text-xs">Coobbligato / Garante <span className="text-slate-400">(opzionale)</span></Label>
                <AnagraficaPicker
                  value={form.garante_id || null}
                  onChange={(id) => setForm({ ...form, garante_id: id })}
                  contextCompanyId={contextCompanyId || null}
                  testIdPrefix="fin-garante"
                />
              </div>
            </div>
          </div>

          {/* ═══ FINANZIAMENTO ═══ */}
          <div data-testid="fin-section-finanziamento">
            <h3 className="text-lg font-bold text-slate-900 mb-3 pb-1 border-b">Finanziamento</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Finanziaria <span className="text-rose-600">*</span></Label>
                <AnagraficaPicker
                  value={form.finanziaria_id || null}
                  onChange={(id) => setForm({ ...form, finanziaria_id: id })}
                  filterCategoria="fornitore_servizi_finanziari.finanziaria"
                  contextCompanyId={contextCompanyId || null}
                  testIdPrefix="fin-anag"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Prodotto / Descrizione <span className="text-rose-600">*</span></Label>
                  <Input value={form.prodotto_descrizione || ""}
                    onChange={(e) => setForm({ ...form, prodotto_descrizione: e.target.value })}
                    placeholder="Es. Prestito personale 48 mesi"
                    data-testid="fin-prodotto" />
                </div>
                <div>
                  <Label className="text-xs">N° Pratica</Label>
                  <Input value={form.numero_pratica || ""}
                    onChange={(e) => setForm({ ...form, numero_pratica: e.target.value })}
                    placeholder="Es. PRAT-12345"
                    data-testid="fin-num-pratica" />
                </div>
                <div>
                  <Label className="text-xs text-blue-700">Importo finanziato <span className="text-rose-600">*</span></Label>
                  <CurrencyInput value={form.importo_finanziato || 0}
                    onChange={(v) => setForm({ ...form, importo_finanziato: v ?? 0 })}
                    data-testid="fin-importo" />
                </div>
                <div>
                  <Label className="text-xs">Spese istruttoria</Label>
                  <CurrencyInput value={form.spese_istruttoria || 0}
                    onChange={(v) => setForm({ ...form, spese_istruttoria: v ?? 0 })}
                    data-testid="fin-spese-istr" />
                </div>
                <div>
                  <Label className="text-xs">Importo assicurazione</Label>
                  <CurrencyInput value={form.importo_assicurazione || 0}
                    onChange={(v) => setForm({ ...form, importo_assicurazione: v ?? 0 })}
                    data-testid="fin-imp-ass" />
                </div>
                <div>
                  <Label className="text-xs">Compagnia assicurativa</Label>
                  <Input value={form.compagnia_assicurativa || ""}
                    onChange={(e) => setForm({ ...form, compagnia_assicurativa: e.target.value })}
                    placeholder="Es. AXA, Generali, ..."
                    data-testid="fin-compagnia" />
                </div>
                <div>
                  <Label className="text-xs">Durata copertura (mesi)</Label>
                  <Input type="number" min="0" value={form.durata_copertura_mesi ?? ""}
                    onChange={(e) => setForm({ ...form, durata_copertura_mesi: e.target.value })}
                    data-testid="fin-cop-mesi" />
                </div>
                <div>
                  <Label className="text-xs">Descrizione assicurazione</Label>
                  <Input value={form.descrizione_assicurazione || ""}
                    onChange={(e) => setForm({ ...form, descrizione_assicurazione: e.target.value })}
                    placeholder="Es. CPI estesa + furto/incendio"
                    data-testid="fin-desc-ass" />
                </div>
              </div>
              {Number(form.importo_assicurazione || 0) > 0 && (
                <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2"
                     data-testid="fin-iva-info-banner">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-700">Codice IVA assicurazione:</span>
                    <span className="font-mono text-slate-900">E10 — Esente art.10</span>
                    <span className="text-[10px] text-slate-400">🔒 fisso</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Polizze assicurative esenti per natura (art.10 DPR 633/72).
                  </p>
                </div>
              )}

              {/* RATE */}
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Rate</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead className="text-[10px] uppercase text-slate-500 border-b">
                      <tr>
                        <th className="text-left py-1 w-[120px]">Periodo</th>
                        <th className="text-center py-1">N° rate</th>
                        <th className="text-center py-1">Importo rata</th>
                        <th className="text-center py-1">TAN %</th>
                        <th className="text-center py-1">TAEG %</th>
                        <th className="text-center py-1">Maxirata</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 font-medium">1° periodo</td>
                        <td className="py-2 px-1">
                          <Input type="number" min="0"
                            value={form.rate_periodo_1.numero_rate || ""}
                            onChange={(e) => setRP1({ numero_rate: e.target.value })}
                            data-testid="fin-rp1-numero" /></td>
                        <td className="py-2 px-1">
                          <CurrencyInput value={form.rate_periodo_1.importo_rata || 0}
                            onChange={(v) => setRP1({ importo_rata: v ?? 0 })}
                            data-testid="fin-rp1-rata" /></td>
                        <td className="py-2 px-1">
                          <Input type="number" step="0.01" min="0" max="100"
                            value={form.rate_periodo_1.tan_percent ?? ""}
                            onChange={(e) => setRP1({ tan_percent: e.target.value })}
                            data-testid="fin-rp1-tan" /></td>
                        <td className="py-2 px-1">
                          <Input type="number" step="0.01" min="0" max="100"
                            value={form.rate_periodo_1.taeg_percent ?? ""}
                            onChange={(e) => setRP1({ taeg_percent: e.target.value })}
                            data-testid="fin-rp1-taeg" /></td>
                        <td className="py-2 px-1">
                          <CurrencyInput value={form.rate_periodo_1.maxirata || ""}
                            onChange={(v) => setRP1({ maxirata: v ?? null })}
                            data-testid="fin-rp1-maxi" /></td>
                      </tr>
                      {show2 && (
                        <tr className="border-b bg-slate-50/40">
                          <td className="py-2 font-medium">2° periodo</td>
                          <td className="py-2 px-1">
                            <Input type="number" min="0"
                              value={form.rate_periodo_2?.numero_rate || ""}
                              onChange={(e) => setRP2({ numero_rate: e.target.value })}
                              data-testid="fin-rp2-numero" /></td>
                          <td className="py-2 px-1">
                            <CurrencyInput value={form.rate_periodo_2?.importo_rata || 0}
                              onChange={(v) => setRP2({ importo_rata: v ?? 0 })}
                              data-testid="fin-rp2-rata" /></td>
                          <td className="py-2 px-1">
                            <Input type="number" step="0.01" min="0" max="100"
                              value={form.rate_periodo_2?.tan_percent ?? ""}
                              onChange={(e) => setRP2({ tan_percent: e.target.value })}
                              data-testid="fin-rp2-tan" /></td>
                          <td className="py-2 px-1">
                            <Input type="number" step="0.01" min="0" max="100"
                              value={form.rate_periodo_2?.taeg_percent ?? ""}
                              onChange={(e) => setRP2({ taeg_percent: e.target.value })}
                              data-testid="fin-rp2-taeg" /></td>
                          <td className="py-2 px-1 text-center text-slate-300 text-[10px] italic">—</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <Button
                    size="sm" variant="outline" className="mt-2"
                    onClick={() => {
                      if (show2) {
                        setShow2(false);
                        setForm((f) => ({ ...f, rate_periodo_2: null }));
                      } else {
                        setShow2(true);
                        setForm((f) => ({ ...f, rate_periodo_2: emptyForm().rate_periodo_2 }));
                      }
                    }}
                    data-testid="fin-toggle-rp2">
                    {show2 ? "− Rimuovi 2° periodo" : "+ Aggiungi 2° periodo"}
                  </Button>
                </div>
              </div>

              {/* PROVVIGIONI */}
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Provvigioni</div>
                <div className="flex items-center gap-5 mb-2">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={isTotale}
                      onChange={() => setForm({ ...form, provvigioni_tipo: "totale" })}
                      data-testid="fin-provv-totale" />
                    <span className="font-medium">Importo Totale</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={isParziali}
                      onChange={() => setForm({ ...form, provvigioni_tipo: "parziali" })}
                      data-testid="fin-provv-parziali" />
                    <span className="font-medium">Importi Parziali</span>
                  </label>
                </div>

                {isTotale && (
                  <div className="grid grid-cols-2 gap-3" data-testid="fin-provv-totale-block">
                    <div>
                      <Label className="text-xs">Importo lordo totale <span className="text-rose-600">*</span></Label>
                      <CurrencyInput value={form.provvigione_lorda_totale ?? 0}
                        onChange={(v) => setForm({ ...form, provvigione_lorda_totale: v ?? 0 })}
                        data-testid="fin-provv-totale-input" />
                    </div>
                  </div>
                )}

                {isParziali && (
                  <div className="space-y-3" data-testid="fin-provv-parziali-block">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Proventi finanziamento</Label>
                        <CurrencyInput value={form.proventi_finanziamento ?? ""}
                          onChange={(v) => setForm({ ...form, proventi_finanziamento: v })}
                          data-testid="fin-provv-finanz" />
                        <div className="text-[10px] text-slate-500 mt-0.5">Provento sulla pratica di finanziamento puro</div>
                      </div>
                      <div>
                        <Label className="text-xs">Proventi spese pratica</Label>
                        <CurrencyInput value={form.proventi_spese_pratica ?? 0}
                          onChange={(v) => setForm({ ...form, proventi_spese_pratica: v })}
                          data-testid="fin-provv-spese" />
                        <div className="text-[10px] text-slate-500 mt-0.5">Pre-impostato a spese istruttoria, editabile</div>
                      </div>
                    </div>
                    <div className="border-t pt-2">
                      <div className="text-[11px] uppercase font-medium text-slate-600 mb-1.5">Proventi assicurazione</div>
                      <div className="flex items-center gap-4 mb-2">
                        <label className="flex items-center gap-1 text-xs cursor-pointer">
                          <input type="radio" checked={ass_tipo === "percentuale"}
                            onChange={() => setForm({ ...form, proventi_assicurazione_tipo: "percentuale",
                                                      proventi_assicurazione_fisso: null })}
                            data-testid="fin-ass-perc" />
                          Percentuale (% su importo)
                        </label>
                        <label className="flex items-center gap-1 text-xs cursor-pointer">
                          <input type="radio" checked={ass_tipo === "fisso"}
                            onChange={() => setForm({ ...form, proventi_assicurazione_tipo: "fisso",
                                                      proventi_assicurazione_percentuale: null })}
                            data-testid="fin-ass-fisso" />
                          Importo fisso
                        </label>
                      </div>
                      {ass_tipo === "percentuale" ? (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">% sul importo assicurazione</Label>
                            <div className="relative">
                              <Input type="number" step="0.1" min="0" max="100"
                                value={form.proventi_assicurazione_percentuale ?? ""}
                                onChange={(e) => setForm({ ...form, proventi_assicurazione_percentuale: e.target.value })}
                                placeholder="0,0" className="pr-7"
                                data-testid="fin-ass-perc-input" />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">%</span>
                            </div>
                          </div>
                        </div>
                      ) : ass_tipo === "fisso" ? (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Importo fisso</Label>
                            <CurrencyInput value={form.proventi_assicurazione_fisso ?? ""}
                              onChange={(v) => setForm({ ...form, proventi_assicurazione_fisso: v })}
                              data-testid="fin-ass-fisso-input" />
                          </div>
                        </div>
                      ) : null}
                      {(ass_perc_valid || ass_fix_valid) && (
                        <div className={`rounded-md px-3 py-1.5 text-[12px] border mt-2 ${PROVV_BG} ${PROVV_BORDER}`}
                             data-testid="fin-ass-anteprima">
                          Su importo assicurazione <span className="font-mono">{formatEUR(ass_imp)}</span>:
                          {" "}provento{" "}<span className={`font-mono font-semibold ${PROVV_TEXT}`}>{formatEUR(ass_calc)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {provv_tipo && (
                  <div className={`mt-3 px-4 py-2 rounded-lg border-2 flex items-center justify-between ${PROVV_BG} ${PROVV_BORDER}`}
                       data-testid="fin-totale-proventi">
                    <span className="font-medium text-slate-700">Totale proventi:</span>
                    <span className={`font-mono font-bold text-lg ${PROVV_TEXT}`}>{formatEUR(totale_proventi)}</span>
                  </div>
                )}
              </div>

              {/* STATO + DATA (Sprint 4F1.4.1) */}
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Stato</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Stato finanziamento</Label>
                    <Select value={form.status_finanziamento || "in_attesa"}
                            onValueChange={(v) => setForm({ ...form, status_finanziamento: v })}>
                      <SelectTrigger data-testid="fin-stato"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_attesa">In attesa</SelectItem>
                        <SelectItem value="approvato">Approvato</SelectItem>
                        <SelectItem value="erogato">Erogato</SelectItem>
                        <SelectItem value="rifiutato">Rifiutato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input type="date"
                      value={form.data || ""}
                      onChange={(e) => setForm({ ...form, data: e.target.value || null })}
                      data-testid="fin-data" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} data-testid="fin-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  // Sprint 4F1.4: deprecato (sostituito da 2 macro-sezioni H3). Mantenuto per compat se usato altrove.
  return (
    <div className="border-t pt-3 first:border-t-0 first:pt-0">
      <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function emptyForm() {
  return {
    intestatario_id: null,
    garante_id: null,
    finanziaria_id: null,
    prodotto_descrizione: "",
    numero_pratica: "",
    importo_finanziato: 0,
    spese_istruttoria: 0,
    importo_assicurazione: 0,
    compagnia_assicurativa: "",
    durata_copertura_mesi: 0,
    descrizione_assicurazione: "",
    rate_periodo_1: { numero_rate: 0, importo_rata: 0,
                     tan_percent: 0, taeg_percent: 0, maxirata: null },
    rate_periodo_2: null,
    provvigioni_tipo: null,
    provvigione_lorda_totale: null,
    proventi_finanziamento: null,
    proventi_spese_pratica: null,
    proventi_assicurazione_tipo: null,
    proventi_assicurazione_percentuale: null,
    proventi_assicurazione_fisso: null,
    status_finanziamento: "in_attesa",
    data: null,
  };
}


/** Sprint 4F1.3 — Vista compatta finanziamento in scheda preventivo/vendita.
 *  Sprint 4F1.4: layout griglia 2x4 (etichette uppercase sopra, valori sotto).
 *  Mostra header + 8 dati + pill `+X € proventi` (se >0).
 */
export function FinanziamentoView({ vendita, providerName }) {
  const f = vendita.finanziamento || {};
  const rp1 = f.rate_periodo_1 || {};
  const rp2 = f.rate_periodo_2;
  const rataDisp = rp2 && rp2.importo_rata
    ? `${formatEUR(rp1.importo_rata)} → ${formatEUR(rp2.importo_rata)}`
    : formatEUR(rp1.importo_rata);
  const durataMesi = (rp1.numero_rate || 0) + (rp2?.numero_rate || 0);
  const stato = f.status_finanziamento || "in_attesa";

  return (
    <div className="space-y-3 text-sm" data-testid="finanziamento-view">
      {/* HEADER: finanziaria a sinistra (pill +€ proventi è spostata
          inline accanto al titolo Card · vedi VenditaDetail) */}
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Finanziaria</div>
        <span className="font-bold text-purple-800 text-base" data-testid="fin-view-finanziaria">
          {providerName || "—"}
        </span>
      </div>

      {/* GRIGLIA 2x4 */}
      <div className="grid grid-cols-4 gap-3 pt-2">
        <FinCell label="Rata"       value={rataDisp} />
        <FinCell label="Durata"     value={durataMesi ? `${durataMesi} mesi` : "—"} />
        <FinCell label="Data"       value={formatDateIT(f.data || f._created_at) || "—"} />
        <FinCell label="Stato">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATO_COLORS[stato]}`}>
            {STATO_LABELS[stato] || stato}
          </span>
        </FinCell>

        <FinCell label="Istruttoria"     value={formatEUR(f.spese_istruttoria)} />
        <FinCell label="€ Assicurazione" value={formatEUR(f.importo_assicurazione)} />
        <FinCell label="Copertura ass."  value={f.durata_copertura_mesi ? `${f.durata_copertura_mesi} mesi` : "—"} />
        <FinCell label="Imp. auto fin.">
          <span className="font-mono font-bold text-lg text-blue-700"
                data-testid="fin-view-importo">
            {formatEUR(f.importo_finanziato)}
          </span>
        </FinCell>
      </div>
    </div>
  );
}

function FinCell({ label, value, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide font-medium text-slate-500">{label}</span>
      {children || (
        <span className="font-mono text-slate-800">{value || "—"}</span>
      )}
    </div>
  );
}

function formatDateIT(isoStr) {
  if (!isoStr) return null;
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return null;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  } catch { return null; }
}
